<template>
  <div class="buy-summary-bar">
    <div
      class="d-flex align-items-center justify-content-between gap-3 flex-wrap"
    >
      <!-- Totals -->
      <div class="d-flex align-items-center gap-4 flex-wrap">
        <div class="d-flex flex-column">
          <span class="eyebrow-label mb-1">{{
            $t("bulk_buy.total_consumption")
          }}</span>
          <span class="summary-metric"
            >{{ totalVolume.toLocaleString() }} MWh</span
          >
        </div>
        <div class="summary-divider"></div>
        <div class="d-flex flex-column">
          <span class="eyebrow-label mb-1">{{
            $t("bulk_buy.destination_companies")
          }}</span>
          <span class="summary-metric">{{ beneficiariesCount }}</span>
        </div>
        <div class="summary-divider"></div>
        <div class="d-flex flex-column">
          <span class="eyebrow-label mb-1">{{
            $t("bulk_buy.estimated_cost")
          }}</span>
          <span class="summary-metric">{{ estimatedCostDisplay }}</span>
          <span v-if="pricedSubline" class="summary-subline">{{
            pricedSubline
          }}</span>
        </div>
      </div>

      <!-- State-driven action -->
      <div class="d-flex align-items-center gap-3">
        <span
          v-if="actionState === 'custom'"
          class="custom-reassurance d-flex align-items-center gap-2 text-secondary small"
        >
          <FontAwesomeIcon
            :icon="['far', 'circle-info']"
            class="flex-shrink-0"
          />
          {{ $t("bulk_buy.custom_quote_reassurance") }}
        </span>
        <button
          type="button"
          class="btn btn-primary d-inline-flex align-items-center gap-2"
          :disabled="isButtonDisabled"
          @click="emit('submit')"
        >
          <span
            v-if="actionState === 'loading'"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          {{ buttonLabel }}
          <FontAwesomeIcon
            v-if="actionState === 'review' || actionState === 'custom'"
            icon="arrow-right"
          />
        </button>
      </div>
    </div>

    <!-- quotes_indicative_note: shown from the first displayed price onward -->
    <div
      v-if="totalKnownVolume > 0"
      class="indicative-note text-secondary small mt-2 d-flex align-items-center gap-2"
    >
      <FontAwesomeIcon :icon="['far', 'circle-info']" class="flex-shrink-0" />
      {{ $t("bulk_buy.quotes_indicative_note") }}
    </div>

    <!-- RFQ notice: any beneficiary priced by hand -->
    <div v-if="agentReachOutCount > 0" class="advisory-note mt-3">
      <FontAwesomeIcon
        :icon="['far', 'circle-info']"
        class="flex-shrink-0 mt-1"
      />
      <div>
        <div class="fw-semibold">
          {{ $t("bulk_buy.rfq_notice") }}:
          {{ agentReachOutVolume.toLocaleString() }} MWh ({{
            $t("bulk_buy.illiquid_companies", agentReachOutCount)
          }})
        </div>
        <div class="small">{{ $t("bulk_buy.rfq_reassurance") }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatEuro } from "@/composables/formatUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { BulkBuyActionState } from "./bulkBuyTypes";

const props = defineProps<{
  totalVolume: number;
  beneficiariesCount: number;
  totalKnownVolume: number;
  totalKnownPrice: number;
  agentReachOutCount: number;
  agentReachOutVolume: number;
  // Derived once by the orchestrator; this footer only renders it.
  actionState: BulkBuyActionState;
  // All beneficiary rows are filled in (gates Ask-for-quote / custom quote).
  canProceed: boolean;
}>();

const emit = defineEmits<{ submit: [] }>();

const { t } = useI18n();

const buttonLabel = computed(() => {
  switch (props.actionState) {
    case "empty":
      return t("bulk_buy.add_at_least_one_beneficiary");
    case "loading":
      return t("bulk_buy.fetching_quotes");
    case "custom":
      return t("bulk_buy.request_custom_quote");
    case "review":
      return t("bulk_buy.review");
    default:
      return t("bulk_buy.ask_for_quote");
  }
});

const isButtonDisabled = computed(() => {
  if (props.actionState === "empty" || props.actionState === "loading")
    return true;
  // ask / custom / review all require every row complete first
  return !props.canProceed;
});

const estimatedCostDisplay = computed(() =>
  props.totalKnownVolume > 0 ? formatEuro(props.totalKnownPrice) : "—"
);

// Under the estimated cost, when part of the order is priced and part is manual.
const pricedSubline = computed(() => {
  if (props.agentReachOutCount === 0 || props.totalKnownVolume === 0) return "";
  const perMwh = props.totalKnownPrice / props.totalKnownVolume;
  return t("bulk_buy.priced_subline", {
    known: props.totalKnownVolume.toLocaleString(),
    total: props.totalVolume.toLocaleString(),
    perMwh: formatEuro(perMwh),
  });
});
</script>

<style scoped lang="scss">
.buy-summary-bar {
  padding: 1rem 1.125rem;
  background: var(--phoenix-emphasis-bg);
  border: 1px solid var(--phoenix-border-color);
  border-radius: 12px;
}

.summary-metric {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: var(--phoenix-body-color);
}

.summary-divider {
  width: 1px;
  align-self: stretch;
  background: var(--phoenix-border-color);
}

.summary-subline {
  margin-top: 0.2rem;
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
  color: var(--phoenix-secondary-color);
}

.custom-reassurance {
  max-width: 15rem;
}
</style>
